import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, NavLink, useBCMSContext, useStateManager } from "@bcm-dev/bcms-vite";
import Classes, { Account, Document } from "model";
import React from "react";

import { CenterContentContainer, ContentHeaderView, PageContainer, PageContentView } from 'components/shared';
import StatementSearchItem from 'components/workflows/StatementSearchWorkflow/classes/StatementSearchItem';
import MobileStatementTable, { MobileStatementsTableRow } from './components/MobileStatementsTable';
import StatementSearch from './components/StatementSearch';
import StatementTable, { StatementView } from './components/StatementTable';
import './mobile.css';
import './styles.css';

/**
 * @function StatementsPage
 * @return {React.Component}
 * @exported true
 * @description The main component for StatementsPage
 */
export default function StatementsPage(props) {
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);

    const data = { ...getDefaultPageData(context), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onAccountChange: value => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "search", "account" ], value === "all" ? value : value.id)
            .submit(setState);
        },

        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update([ "data", "search", prop ], value)
                .submit(setState);
        },

        onUpdate: value => {
            BCMSStateManagerCRUD.init(state)
                .update([ "data", "search" ], value)
                .update([ "ui", "searchAccordion", "isActive" ], false)
                .submit(setState);
        },

        onAccordionToggle: value => {
            BCMSStateManagerCRUD.init(state)
                .update([ "ui", "searchAccordion", "isActive" ], value)
                .update([ "ui", "advancedSearch", "isActive" ], false)
                .submit(setState);
        },

        onAdvancedSearchToggle: value => {
            const crud = BCMSStateManagerCRUD.init(state);

            if(state.ui.searchAccordion.isActive === false) {
                crud.update([ "ui", "advancedSearch", "isActive" ], true);
            } else {
                crud.update([ "ui", "advancedSearch", "isActive" ], value);
            }

            crud.update([ "ui", "searchAccordion", "isActive" ], true);
            crud.submit(setState);
        }
    });

    return (
        <PageContainer className="statements-page" state={ { data, ui } } controller={ controller }>
            <StatementsContent />
        </PageContainer>
    );
}

export const getDefaultPageData = (controller) => {
    let branding = {};
    let options = {}
    let showAllAccountsOption = true;
    const firstAccountID = Account.initAll()[0].id;
    const documents = Document.sortDateAscending(Document.initAll());
    
    if(controller) {
        branding = controller.state.branding;
        options = controller.state.branding.options;
        showAllAccountsOption = options.allaccts_statements_abi22 === "true";
    }

    return {
        search: StatementSearchItem.init({ 
            account: showAllAccountsOption ? "all" : `${ Account.initAll()[0].id }`,
            filtered: showAllAccountsOption ? documents : documents.filter(item => item.account.id === firstAccountID)
        }),
        branding: branding
    };
};
export const getDefaultPageUI = () => ({
    searchAccordion: { isActive: true },
    advancedSearch: { isActive: false },
    accountDD: { isActive: false }, 
    startDate: { isActive: false },
    endDate: { isActive: false },
    documentDD: { isActive: false },
    categoryDD: { isActive: false }
});

function StatementsContent(props) {
    const { data } = useStateManager();

    const statementRows = data.search.filtered.map((document, index) => <StatementView
        key={ `statement_${ document.id }` }
        link={ 
            <DisabledPrompt>
                <NavLink dataPoint={ `Statement_Item_${ index }` }>
                    { document.date.format("Y-m-d") }
                </NavLink> 
            </DisabledPrompt>
        }
        id={ document.id }
        accountNickname={ `${ document.account.nickname } ${ document.account.number }` }
        accountNumber={ document.account.number }
        documentName={ document.name }
        documentType={ document.type }
    />, []);

    // Sort all documents by ascending date, then map over all Documents returning a new MobileStatementsTableRow component
    // setting all required properties
    const mobileStatementRows = data.search.filtered.map((document, index) => <MobileStatementsTableRow
        key={ `mobile_statement_${ document.id }` }
        link={ 
        <DisabledPrompt>
            <NavLink className="top-row-text" dataPoint={ `Statement_Item_${ index }` }>
            { document.date.format("Y-m-d") }
            </NavLink> 
        </DisabledPrompt>
        }
        id={ document.id }
        accountNickname={ `${ document.account.nickname } ${ document.account.number }` }
        accountNumber={ document.account.number }
        documentName={ document.name }
        documentType={ document.type }
    />, []);

    return (
        <PageContentView>
            <CenterContentContainer>
                <ContentHeaderView text="E-Statements & Notices" />
                {/* Search Form */}
                <StatementSearch />
                {/* Search Accordion Here */}
                <div className="show-on-mobile">
                    {
                        statementRows.length === 0
                        ? <NoAvailableDocumentsWidget />
                        : <MobileStatementTable>{ mobileStatementRows }</MobileStatementTable>
                    }
                </div>
                <div className="show-on-desktop">
                    {
                        statementRows.length === 0
                        ? <NoAvailableDocumentsWidget />
                        : <StatementTable>{ statementRows }</StatementTable>
                    }
                </div>
            </CenterContentContainer>
        </PageContentView>
    );
}

/**
 * @function NoAvailableDocumentsWidget
 * @return {React.Component}
 * @exported false
 * @description Shows when no documents are available.
 * @example
 * <NoAvailableDocumentsWidget />
 */

function NoAvailableDocumentsWidget(props) {
    return(
      <div className="no-available-documents-widget">
        <p className="no-available-documents-text text-center">No documents available.</p>
      </div>
    );
  }